import { NgModule } from '@angular/core'
import { RouterModule, Routes } from '@angular/router'
import { COURSE_SLUGS } from './features/courses/course-slugs'
import { EVENT_SLUGS } from './features/events/event-details/event-slugs'

/**
 * Angular's anchor scrolling uses window.scrollTo and ignores CSS
 * scroll-margin, so "#section" links landed under the fixed header and the
 * sticky "on this page" bar. Offset by whatever is pinned at the top:
 * the jump bar (its sticky top + height) when the page has one, otherwise
 * the fixed header.
 */
export function anchorScrollOffset(): [number, number] {
  // A section's own CSS scroll-margin-top wins, so the landing gap under the
  // jump bar is set in one place (styles.scss: .page-jump ~ section[id])
  const id = decodeURIComponent(window.location.hash.slice(1))
  const target = id ? document.getElementById(id) : null
  const margin = target ? parseFloat(getComputedStyle(target).scrollMarginTop) || 0 : 0
  if (margin) return [0, margin]

  const gap = 16
  const jump = document.querySelector<HTMLElement>('nav[class$="-jump"], nav[class*="-jump "]')
  if (jump) {
    const stickyTop = parseFloat(getComputedStyle(jump).top) || 0
    return [0, stickyTop + jump.offsetHeight + gap]
  }
  const header = document.querySelector<HTMLElement>('app-header header')
  return [0, (header?.offsetHeight ?? 90) + gap]
}

const routes: Routes = [
  {
    path: '',
    loadChildren: () =>
      import('./features/home/home.module').then(m => m.HomeModule)
  },
  {
    path: 'about',
    loadChildren: () =>
      import('./features/about/about.module').then(m => m.AboutModule)
  },
  {
    path: 'admission',
    loadChildren: () =>
      import('./features/admission/admission.module').then(
        m => m.AdmissionModule
      )
  },
  {
    path: 'innovation',
    loadChildren: () =>
      import('./features/innovation/innovation.module').then(
        m => m.InnovationModule
      )
  },
  {
    path: 'training-placement',
    loadChildren: () =>
      import('./features/training-placement/training-placement.module').then(
        m => m.TrainingPlacementModule
      )
  },
  {
    path: 'campus-life',
    loadChildren: () =>
      import('./features/campus-life/campus-life.module').then(
        m => m.CampusLifeModule
      )
  },
  {
    path: 'programs-offered',
    loadChildren: () =>
      import('./features/programs-offered/programs-offered.module').then(
        m => m.ProgramsOfferedModule
      )
  },
  // Old ghru.edu.in URL (program-offered?tab=pg); query params carry over
  { path: 'program-offered', redirectTo: 'programs-offered' },
  // Old ghru.edu.in URL
  { path: 'ncc', redirectTo: 'humberger-menu/ncc' },
  { path: 'rawry', redirectTo: 'humberger-menu/rawry' },
  { path: 'bachelor-computer-application', redirectTo: 'bca' },
  // The live Fees Structure page only lists the fee bank account
  { path: 'fees-structure', redirectTo: 'admission/bank-details' },
  {
    path: 'schools',
    loadChildren: () =>
      import('./features/schools/schools.module').then(m => m.SchoolsModule)
  },
  {
    path: 'academics-and-research',
    loadChildren: () =>
      import(
        './features/academics-and-research/academics-and-research.module'
      ).then(m => m.AcademicsAndResearchModule)
  },
  {
    path: 'humberger-menu',
    loadChildren: () =>
      import('./features/humberger-menu/humberger-menu.module').then(
        m => m.HumbergerMenuModule
      )
  },
  {
    path: 'blogs',
    loadChildren: () =>
      import('./features/blogs/blogs.module').then(m => m.BlogsModule)
  },
  {
    path: 'events',
    loadChildren: () =>
      import('./features/events/events.module').then(m => m.EventsModule)
  },
  {
    path: 'contact',
    loadChildren: () =>
      import('./features/contact/contact.module').then(m => m.ContactModule)
  },
  // Course pages live at the site root (e.g. /diploma-computer-science-engg),
  // matching the URLs on ghru.edu.in
  ...COURSE_SLUGS.map(slug => ({
    path: slug,
    data: { slug },
    loadChildren: () =>
      import('./features/courses/courses.module').then(m => m.CoursesModule)
  })),
  // merged repeat events: old URLs go to the combined page
  { path: 'cadits-night-farewell-party', redirectTo: 'farewell-party' },
  { path: 'induction-program', redirectTo: 'orientation-programme' },
  // Event detail pages also live at the site root (e.g. /ganesh-utsav)
  ...EVENT_SLUGS.map(slug => ({
    path: slug,
    data: { slug },
    loadComponent: () =>
      import('./features/events/event-details/event-details.component').then(
        m => m.EventDetailsComponent
      )
  })),
  {
    path: '**',
    redirectTo: ''
  }
]

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      scrollPositionRestoration: 'top',
      anchorScrolling: 'enabled',
      scrollOffset: anchorScrollOffset,
      onSameUrlNavigation: 'reload'
    })
  ],
  exports: [RouterModule]
})
export class AppRoutingModule {}
